import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import './NotificationToast.css';

export interface ToastNotification {
    id: string;
    type: 'collab_invite' | 'friend_request' | 'achievement' | 'message';
    title: string;
    message: string;
    sender?: string;
}

interface NotificationToastProps {
    notifications: ToastNotification[];
    onDismiss: (id: string) => void;
    onAction?: (notification: ToastNotification) => void;
}

const getIcon = (type: ToastNotification['type']) => {
    switch (type) {
        case 'collab_invite':
            return '🎉';
        case 'friend_request':
            return '👋';
        case 'achievement':
            return '🏆';
        case 'message':
        default:
            return '💬';
    }
};

export function NotificationToast({ notifications, onDismiss, onAction }: NotificationToastProps) {
    return (
        <div className="notification-toast-container">
            <AnimatePresence mode="popLayout">
                {notifications.map((notification) => (
                    <ToastItem
                        key={notification.id}
                        notification={notification}
                        onDismiss={onDismiss}
                        onAction={onAction}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
}

interface ToastItemProps {
    notification: ToastNotification;
    onDismiss: (id: string) => void;
    onAction?: (notification: ToastNotification) => void;
}

function ToastItem({ notification, onDismiss, onAction }: ToastItemProps) {
    // Auto-dismiss after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(notification.id);
        }, 5000);

        return () => clearTimeout(timer);
    }, [notification.id, onDismiss]);

    const handleClick = () => {
        if (onAction) {
            onAction(notification);
        }
        onDismiss(notification.id);
    };

    return (
        <motion.div
            className={`notification-toast notification-toast-${notification.type}`}
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            onClick={handleClick}
            layout
        >
            <span className="notification-toast-icon">{getIcon(notification.type)}</span>
            <div className="notification-toast-content">
                <span className="notification-toast-title">{notification.title}</span>
                <span className="notification-toast-message">{notification.message}</span>
            </div>
            <button
                className="notification-toast-close"
                onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(notification.id);
                }}
            >
                ×
            </button>
        </motion.div>
    );
}

export default NotificationToast;
